import React, { memo } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { Box, Fade, RadioGroup } from '@material-ui/core'
import TYPES, { FLEX } from '@v4/constants/quiz'
import EditableHTML from '@v4/components/common/EditableHTML'
import withWhiteTheme from '@v4/components/sections/Question/Components/withWhiteTheme'
import PrimaryThemeProvider from '@v4/components/sections/Question/Components/PrimaryThemeProvider'
import CheckboxButton from '../../components/CheckboxButton'
import RadioButton from '../../components/RadioButton'
import SubmitButton from '../../components/SubmitButton'
import EditableLabel from './components/EditableLabel'
import { useRangeRadioButtons } from './RangeRadioButtons.hooks'

const RangeRadioButtons = props => {
	const [
		{
			classes,
			type,
			dividedGroups,
			isMobile,
			selectedValue,
			pending,
			useOldCheckboxes,
			justify,
			labels,
			shadow,
			range,
			buttonVariant,
		},
		{ handleChange, handleKeyUp, handleSubmit, handleChangeLabel },
	] = useRangeRadioButtons(props)

	return (
		<Box className={clsx(classes[type], classes.root, 'q-scale')}>
			{type === TYPES.DIVIDED_SCALE && (
				<Box className={clsx(classes.table, 'q-scale-table')}>
					{_.map(dividedGroups, ({ key: groupKey, items }, index) => (
						<div className={clsx(classes.cell, 'q-scale-cell')} key={groupKey}>
							<RadioGroup
								color="primary"
								className={clsx(classes.group, 'q-scale-group')}
								row={!isMobile}
								aria-label="answers"
								name="answers"
								value={String(selectedValue ?? '')}
								onChange={handleChange}
							>
								{_.map(items, ({ key, text, value }, itemIndex) => (
									<Fade key={key} in timeout={itemIndex * 100 + 300}>
										{
											{
												radio: (
													<RadioButton
														className={classes.radioLabel}
														value={value}
														checked={value === selectedValue}
														disabled={pending}
														onKeyUp={handleKeyUp}
														label={text}
														labelPlacement={isMobile ? 'start' : 'bottom'}
														shadow={shadow}
														useOldCheckboxes={useOldCheckboxes}
														classes={{
															control: classes.control,
															label: classes.label,
															radio: classes.radio,
														}}
													/>
												),
												checkbox: (
													<CheckboxButton
														value={value}
														checked={value === selectedValue}
														disabled={pending}
														onChange={handleChange}
														onKeyUp={handleKeyUp}
														label={text}
														labelPlacement={isMobile ? 'start' : 'bottom'}
														shadow={shadow}
														useOldCheckboxes={useOldCheckboxes}
														classes={{
															control: classes.control,
															label: classes.label,
															checkbox: classes.checkbox,
														}}
													/>
												),
											}[buttonVariant]
										}
									</Fade>
								))}
							</RadioGroup>
							<EditableLabel
								className={classes.editableLabel}
								value={_.get(labels, [index, 'text'])}
								index={index}
								shadow={shadow}
								onChange={handleChangeLabel}
							/>
						</div>
					))}
				</Box>
			)}

			{type === TYPES.RADIO && (
				<Box className={clsx(classes.content, 'q-radio-content')}>
					<RadioGroup
						color="primary"
						className={clsx(classes.row, 'q-radio-group')}
						row
						aria-label="answers"
						name="answers"
						value={String(selectedValue ?? '')}
						onChange={handleChange}
					>
						{_.map(range, ({ key, text, value }, index) => (
							<Fade key={key} in timeout={index * 100 + 300}>
								{
									{
										radio: (
											<RadioButton
												value={value}
												checked={value === selectedValue}
												disabled={pending}
												onKeyUp={handleKeyUp}
												label={text}
												labelPlacement="top"
												shadow={shadow}
												useOldCheckboxes={useOldCheckboxes}
												classes={{
													control: classes.control,
													label: classes.label,
													radio: classes.radio,
												}}
											/>
										),
										checkbox: (
											<CheckboxButton
												value={value}
												checked={value === selectedValue}
												disabled={pending}
												onChange={handleChange}
												onKeyUp={handleKeyUp}
												label={text}
												labelPlacement="top"
												shadow={shadow}
												useOldCheckboxes={useOldCheckboxes}
												classes={{
													control: classes.control,
													label: classes.label,
													checkbox: classes.checkbox,
												}}
											/>
										),
									}[buttonVariant]
								}
							</Fade>
						))}
					</RadioGroup>
					{_.some(labels, ({ text }) => text) && (
						<Box
							className={clsx(classes.marks, 'q-radio-marks')}
							display="flex"
							justifyContent="space-between"
						>
							{_.map(labels, ({ text }, index) =>
								index === 1 && _.size(labels) === 2 ? null : (
									<EditableHTML
										variant="body1"
										html={text}
										displayEmpty
										key={index}
										className={clsx(classes.mark, 'question-radio-mark')}
										component="span"
										shadow={shadow}
									/>
								),
							)}
						</Box>
					)}
				</Box>
			)}

			<Box className="q-scale-button-container" display="flex" mt={4} justifyContent={justify}>
				<PrimaryThemeProvider>
					<SubmitButton loading={pending} className="q-scale-button" onClick={handleSubmit} />
				</PrimaryThemeProvider>
			</Box>
		</Box>
	)
}

RangeRadioButtons.propTypes = {
	questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	type: PropTypes.oneOf([TYPES.DIVIDED_SCALE, TYPES.RADIO]),
	justify: PropTypes.oneOf(Object.values(FLEX)),
	onClick: PropTypes.func,
	pending: PropTypes.bool,
	useOldCheckboxes: PropTypes.bool,
	shadow: PropTypes.bool,
	mediaPosition: PropTypes.string,
	showMedia: PropTypes.bool,
}

RangeRadioButtons.defaultProps = {
	type: TYPES.DIVIDED_SCALE,
	justify: FLEX.START,
	pending: false,
	useOldCheckboxes: false,
	onClick: () => {},
}

export default memo(withWhiteTheme(RangeRadioButtons))
